import React from "react";
import {
  Alert,
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

export default function AdvancedFilterForm(props) {
  const { filters, onApply, handleClose } = props;
  const savedRows = Object.entries(filters).map(([key, value]) => ({
    key,
    value,
  }));
  const [rows, setRows] = React.useState(
    savedRows.length ? savedRows : [{ key: "", value: "" }],
  );
  const [error, setError] = React.useState("");

  const updateRow = (index, field, value) => {
    setRows((prevRows) =>
      prevRows.map((row, rowIndex) =>
        rowIndex === index ? { ...row, [field]: value } : row,
      ),
    );
    setError("");
  };

  const applyFilters = (nextFilters) => {
    handleClose();
    onApply(nextFilters);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const filledRows = rows
      .map(({ key, value }) => ({ key: key.trim(), value: value.trim() }))
      .filter(({ key, value }) => key || value);

    if (!filledRows.length) {
      setError("Enter a variable name and a value.");
    } else if (filledRows.some(({ key, value }) => !key || !value)) {
      setError("Every row needs both a variable name and a value.");
    } else if (
      new Set(filledRows.map(({ key }) => key)).size !== filledRows.length
    ) {
      setError("Each variable name can only be used once.");
    } else {
      applyFilters(
        Object.fromEntries(filledRows.map(({ key, value }) => [key, value])),
      );
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      onKeyDown={(e) => e.key !== "Escape" && e.stopPropagation()}
    >
      <Stack spacing={3} mt={1}>
        <Alert severity="info" icon={false}>
          <Typography variant="body2" fontWeight={500} mb={0.5}>
            Find credentials by the values filled into their template variables.
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            <Typography variant="body2" component="li">
              <b>Variable name:</b> type it exactly as it appears in your
              template or CSV column, including capital letters and spaces. For
              example, <b>Course</b> or <b>Recipient Name</b>. Searching for{" "}
              <b>course</b> will not find <b>Course</b>.
            </Typography>
            <Typography variant="body2" component="li">
              <b>Value:</b> type the complete value. Capital letters do not
              matter, but part of a value will not match. <b>aditya</b> finds{" "}
              <b>Aditya</b>, but not <b>Aditya Raj</b>.
            </Typography>
            <Typography variant="body2" component="li">
              <b>More than one variable:</b> a credential is shown only if it
              matches all of them.
            </Typography>
            <Typography variant="body2" component="li">
              The filter applies to the tab and records you are viewing. Export
              All will export only the filtered credentials.
            </Typography>
            <Typography variant="body2" component="li">
              Uploaded PDFs usually have no variables, so they may not appear in
              the results.
            </Typography>
          </Box>
        </Alert>

        <Stack spacing={2}>
          {rows.map((row, index) => (
            <Stack
              key={index}
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              alignItems={{ sm: "center" }}
            >
              <TextField
                label="Variable name"
                placeholder="Course"
                size="small"
                fullWidth
                value={row.key}
                onChange={(e) => updateRow(index, "key", e.target.value)}
              />
              <TextField
                label="Value"
                placeholder="Data Science"
                size="small"
                fullWidth
                value={row.value}
                onChange={(e) => updateRow(index, "value", e.target.value)}
              />
              {rows.length > 1 && (
                <Tooltip title="Remove">
                  <IconButton
                    aria-label="Remove variable"
                    onClick={() =>
                      setRows((prevRows) =>
                        prevRows.filter((_, rowIndex) => rowIndex !== index),
                      )
                    }
                    sx={{ alignSelf: { xs: "flex-end", sm: "center" } }}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          ))}
          <Box>
            <Button
              size="small"
              startIcon={<AddOutlinedIcon />}
              onClick={() =>
                setRows((prevRows) => [...prevRows, { key: "", value: "" }])
              }
            >
              Add another variable
            </Button>
          </Box>
          {error && (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          )}
        </Stack>

        <Stack direction="row" spacing={2} justifyContent="flex-end">
          {savedRows.length > 0 && (
            <Button
              color="error"
              onClick={() => applyFilters({})}
              sx={{ mr: "auto !important" }}
            >
              Clear Filter
            </Button>
          )}
          <Button onClick={handleClose}>Cancel</Button>
          <Button variant="contained" type="submit">
            Apply
          </Button>
        </Stack>
      </Stack>
    </form>
  );
}
